import { useState, useEffect } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';

export type SubscriptionTier = 'FREE' | 'PREMIUM';

interface PremiumStatus {
  isAuthenticated: boolean;
  isLoading: boolean;
  tier: SubscriptionTier;
  isPremium: boolean;
  userId: string | null;
  error: Error | null;
}

const client = generateClient<Schema>();

export function usePremiumStatus(): PremiumStatus {
  const [status, setStatus] = useState<PremiumStatus>({
    isAuthenticated: false,
    isLoading: true,
    tier: 'FREE',
    isPremium: false,
    userId: null,
    error: null,
  });

  useEffect(() => {
    checkPremiumStatus();
  }, []);

  async function checkPremiumStatus() {
    try {
      // Check if user is authenticated
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      
      if (!user || !session.tokens) {
        setStatus({
          isAuthenticated: false,
          isLoading: false,
          tier: 'FREE',
          isPremium: false,
          userId: null,
          error: null,
        });
        return;
      }

      const userId = user.userId;

      // Fetch user profile to check subscription tier
      const { data: userProfile } = await client.models.UserProfile.get({ owner: userId });

      const tier = userProfile?.subscriptionTier || 'FREE';

      setStatus({
        isAuthenticated: true,
        isLoading: false,
        tier: tier as SubscriptionTier,
        isPremium: tier === 'PREMIUM',
        userId,
        error: null,
      });
    } catch (error) {
      // User not authenticated or error fetching profile
      setStatus({
        isAuthenticated: false,
        isLoading: false,
        tier: 'FREE',
        isPremium: false,
        userId: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      });
    }
  }

  return status;
}

// Helper hook to check if a specific feature is accessible
export function useFeatureAccess(featureId: string): { hasAccess: boolean; isLoading: boolean } {
  const { isPremium, isLoading: statusLoading } = usePremiumStatus();
  const [featureLoading, setFeatureLoading] = useState(true);
  const [featureTier, setFeatureTier] = useState<SubscriptionTier>('FREE');

  useEffect(() => {
    async function checkFeature() {
      try {
        const { data: feature } = await client.models.FeatureAccess.get({ featureId });
        if (feature) {
          setFeatureTier((feature.tier || 'FREE') as SubscriptionTier);
        }
      } catch {
        // Feature not found, assume FREE
        setFeatureTier('FREE');
      } finally {
        setFeatureLoading(false);
      }
    }
    checkFeature();
  }, [featureId]);

  const isLoading = statusLoading || featureLoading;
  const hasAccess = featureTier === 'FREE' || isPremium;

  return { hasAccess, isLoading };
}
